import { useTheme, createTheme as createDSTheme } from 'app/shopify';
import { OfferHubQueueProvider } from 'app/screens/offer-hub/queue-provider';
import { useFonts } from '@expo-google-fonts/inter';
import type { FirebaseMessagingTypes } from '@react-native-firebase/messaging';
import { getMessaging, onMessage, setBackgroundMessageHandler } from '@react-native-firebase/messaging';
import type { LinkingOptions } from '@react-navigation/native';
import { DefaultTheme, NavigationContainer, getPathFromState, getStateFromPath } from '@react-navigation/native';
import * as SentryMobile from '@sentry/react-native';
import { focusManager, useIsRestoring } from '@tanstack/react-query';
import {
  appStartedLoggedAtom,
  hasPushNotificationsEnabledAtom,
  isLoggedInAtom,
  isLoggingOutAtom,
  isOnboardingAtom,
  isStartingAtom,
  lastNotificationResponseAtom,
  userInfoAtom,
  utmParamsAtom,
} from 'app/atoms';
import { AppWithProviders } from 'app/components/app/app-with-providers';
import { DeviceGate } from 'app/components/app/device-gate';
import { GuestWebLoginHost } from 'app/components/app/guest-web-login-host';
import { ToastProvider } from 'app/components/toast-provider';
import { TutorialOverlay } from 'app/components/tutorial-overlay';
import { AppDownloadPromptHost } from 'app/components/app-download-prompt-host';

import { useCustomerIO } from 'app/hooks/customerIO';
import { initializeSentry, useSentry } from 'app/hooks/sentry';
import { useLastNotificationResponse } from 'app/hooks/use-last-notification-response';
import { useApi401Logout } from 'app/hooks/use-logout-routine';
import { useApiRegionMigration } from 'app/hooks/use-api-region-migration';
import { usePaywallAppLaunch } from 'app/hooks/use-paywall-app-launch';
import { useStripeCallback } from 'app/hooks/use-stripe-callback';
import { CustomFacebookEvent, Event, identify, screen, track, trackOnFacebook } from 'app/lib/analytics';
import { http } from 'app/lib/axios';
import { hydrateSessionState, updateSessionState } from 'app/lib/axios/session-state';
import { setBadgeCount } from 'app/lib/badge';
import { env } from 'app/lib/env';
import 'app/lib/form';
import { pushGoogleTagManagerPageView } from 'app/lib/google-tag-manager';
import { getDeviceLanguage, getGuestWebLanguageFromLocation } from 'app/lib/language';
import { createLandingUtmTracker, removeFocusedRoutePath } from 'app/lib/landing-utm';
import { log, recordError } from 'app/lib/monitoring';
import { hasNotificationPermission as checkNotificationPermission, onRemoteMessage } from 'app/lib/notifications';
import { lockStartupOrientation } from 'app/lib/orientation';
import { pushNotifRegistrationInstance } from 'app/lib/push-notifications';
import { queryClient } from 'app/lib/react-query';
import { navigationRef, notifyNavigationReady } from 'app/lib/root-navigation';
import { StorageKey, storage, useStoredValue } from 'app/lib/storage';
import { i18n } from 'app/localization/i18n';
import { useDiscoverScreen, useRefetchBff, useUserProfile } from 'app/network/bff';
import { useCountUnreadMessages, useMessagesPushWatcher } from 'app/network/chat/message';
import { bffKeys, interestKeys, listingKeys } from 'app/network/keys';
import { useMyActivePlans } from 'app/network/plan';
import { useOnePreference, usePreferences } from 'app/network/preferences';
import { recommendationKeys } from 'app/network/recommender';
import { useUser } from 'app/network/user';
import { initialRouteName as initialRouteNameChat } from 'app/screens/chat/navigator';
import { initialRouteName as initialRouteNameManagement } from 'app/screens/management/navigator';
import { initialRouteName as initialRouteNameProfile } from 'app/screens/profile/navigator';
import * as root from 'app/screens/root-stack';
import type { ScreenParameters } from 'app/types/navigation';
import type { DMData } from 'app/types/notification';
import { changeAppIcon } from 'app/utils/helpers';
import { isMobileNative } from 'app/utils/web-helpers';
import { CustomerIO } from 'customerio-reactnative';
import * as Linking from 'expo-linking';
import * as Notifications from 'expo-notifications';
import type { Notification } from 'expo-notifications/src/Notifications.types';
import * as SystemUI from 'expo-system-ui';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import React, { useEffect, useEffectEvent, useRef, useState } from 'react';
import type { AppStateStatus, ColorValue } from 'react-native';
import { AppState, Platform, View } from 'react-native';
const lightTheme = createDSTheme();
const APP_STARTED_DELAY_MS = 5000;
const APP_BACKGROUND_REFETCH_THRESHOLD_MS = 30_000;
const APP_STARTED_AT = Date.now();

const initialTabRoutes = {
  chat: initialRouteNameChat,
  management: initialRouteNameManagement,
  profile: initialRouteNameProfile,
} as Record<string, string>;

Notifications.setNotificationHandler({
  handleNotification: async (notification: Notification) => {
    // Note: Notifications.scheduleNotificationAsync() will only work (display a notif) if shouldShowAlert === true
    // trigger.type === 'unknown' is when we we manually use Notifications.scheduleNotificationAsync({ ..., trigger: null }), i.e. when we really want to display this notif.
    // trigger.type === 'timeInterval' is also a case where we want to display this notif - this is when we want to display a received chat push
    const shouldShowAlert = Boolean(
      Platform.OS === 'android' ||
      (notification.request.trigger &&
        'type' in notification.request.trigger &&
        (notification.request.trigger.type === 'unknown' ||
          notification.request.trigger.type === Notifications.SchedulableTriggerInputTypes.TIME_INTERVAL)),
    );

    return {
      shouldShowBanner: shouldShowAlert,
      shouldShowList: shouldShowAlert,
      shouldPlaySound: Platform.OS === 'android',
      shouldSetBadge: true,
    };
  },
});

if (isMobileNative) {
  setBackgroundMessageHandler(getMessaging(), async (remoteMessage: FirebaseMessagingTypes.RemoteMessage) => {
    if (Platform.OS === 'android') {
      // For FCM (Android), when app is NOT in foreground
      log('[Firebase] Push Notif received from FCM, in background', remoteMessage);
      await onRemoteMessage(remoteMessage);

      // Signal CIO that this notification has been delivered
      await CustomerIO.pushMessaging.onMessageReceived(remoteMessage, false);
    }

    if (Platform.OS === 'ios') {
      // Doesn't work... "[Expo] Push Notif received from APNS, in background" is called, so meh
      log('[Firebase] Push Notif received from APNS, in background', remoteMessage);
    }
  });
}

const prefix = Linking.createURL('');
const billboardListingRegex = new RegExp(
  '/main/billboard/[0-9A-Fa-f]{8}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{12}',
);

// To remove white screen just before the splash screen loads
SystemUI.setBackgroundColorAsync(isMobileNative ? (lightTheme.colors['white-label.splash'] as ColorValue) : null).catch(
  () => {
    // Activity may be unavailable during background launches; safe to ignore
  },
);
void lockStartupOrientation().catch((error: unknown) => log('[App] Unable to lock startup orientation', error));

function App() {
  const theme = useTheme();
  const routeNameRef = useRef<keyof ScreenParameters>(null);
  const setIsLoggedIn = useSetAtom(isLoggedInAtom);
  const setIsStarting = useSetAtom(isStartingAtom);
  const setIsOnboarding = useSetAtom(isOnboardingAtom);
  const userInfo = useAtomValue(userInfoAtom);
  const isRestoringQueryCache = useIsRestoring();
  const [sessionHydrated, setSessionHydrated] = useState(false);
  const [initialUrlLanguage] = useState(getGuestWebLanguageFromLocation);
  const [guestWebLanguage, setGuestWebLanguage] =
    useState<ReturnType<typeof getGuestWebLanguageFromLocation>>(undefined);
  // Ref: https://docs.stripe.com/payments/save-and-reuse?platform=react-native&mobile-ui=payment-element#set-up-return-url
  const handleURLCallback = useStripeCallback();

  const notificationResponse = useLastNotificationResponse();

  useFonts(
    isMobileNative
      ? {}
      : {
          Inter: 'https://rsms.me/inter/font-files/InterVariable.woff2?v=4.1',
          InterItalic: 'https://rsms.me/inter/font-files/InterVariable-Italic.woff2?v=4.1',
        },
  );

  usePreferences();
  useApi401Logout();
  useApiRegionMigration();

  useEffect(() => {
    let ignore = false;

    const checkForAccessToken = async () => {
      const session = await hydrateSessionState();
      if (ignore) return;

      if (session.accessToken) {
        setIsLoggedIn(true);
        setIsOnboarding(!!userInfo && !userInfo.acceptedGuidelineDate);
      } else {
        setGuestWebLanguage(initialUrlLanguage);
      }
      setSessionHydrated(true);
    };
    void checkForAccessToken();

    return () => {
      ignore = true;
    };
  }, [initialUrlLanguage, setIsLoggedIn, setIsOnboarding, userInfo]);

  useEffect(() => {
    if (sessionHydrated && !isRestoringQueryCache) setIsStarting(false);
  }, [isRestoringQueryCache, sessionHydrated, setIsStarting]);

  const [handledDeepLink, setHandledDeepLink] = useState(false);

  const setUtmParams = useSetAtom(utmParamsAtom);
  const landingUtmTracker = useRef(createLandingUtmTracker()).current;

  const linking: LinkingOptions<ScreenParameters> = {
    prefixes: [prefix, `https://${env.UNIVERSAL_LINK_HOSTNAME}`],
    config: {
      screens: root.linking.screens!,
    },
    getStateFromPath(path, options) {
      const appWasStartedWithThisLink = !routeNameRef.current || routeNameRef.current === 'splash';

      console.log('[App.getStateFromPath] path:', path, 'appWasStartedWithThisLink:', appWasStartedWithThisLink);

      // Strip UTM params from the path before passing to React Navigation.
      // UTM params are analytics-only and must not be stored in navigation state:
      // if they were kept, React Navigation would embed them in route.path with the
      // original encoding (e.g. "+" for spaces). On subsequent calls it re-parses the
      // path with a different encoding ("%20"), the comparison fails, and the fallback
      // path it builds lacks a leading "/", causing window.history.replaceState to
      // receive a cross-origin URL → SecurityError.
      const url = new URL(path, 'https://partage.club'); // dummy base URL is needed because link doesn't contain scheme & hostname
      const utmParams: Record<string, string> = {};
      // Collect utm_* keys first to avoid mutating searchParams while iterating it
      const utmKeys = [...new Set(url.searchParams.keys())].filter((key) => key.startsWith('utm_'));
      for (const key of utmKeys) {
        utmParams[key] = url.searchParams.get(key)!;
        url.searchParams.delete(key);
      }
      // Reconstruct path without UTM params (keep other query params intact)
      const cleanPath = url.pathname + url.search + url.hash;
      // Use the default logic; ref: https://reactnavigation.org/docs/navigation-container#linkinggetstatefrompath
      const state = getStateFromPath(cleanPath, options);

      // React Navigation otherwise reuses route.path verbatim, bypassing our
      // serializer and dropping the campaign parameters from the browser URL.
      if (!isMobileNative && state && utmKeys.length > 0) {
        removeFocusedRoutePath(state);
      }

      if (!appWasStartedWithThisLink) {
        console.log('[App.getStateFromPath] returning state:', JSON.stringify(state));
        return state;
      }

      // Only capture UTM params when the app is being opened via this link,
      // not on the repeated verification calls React Navigation makes internally.
      if (Object.keys(utmParams).length > 0) {
        console.log('[App.getStateFromPath] UTM params:', utmParams);
        landingUtmTracker.capture(utmParams);
        setUtmParams(utmParams);
      }

      if (billboardListingRegex.test(cleanPath)) {
        // /main/billboard/[listing_id]
        console.log('[App.getStateFromPath] is Billboard Listing; navigating to listing details screen');
        const id = url.pathname.split('/').pop();
        return getBillboardThenListingRouting(id!);
      }

      if (state?.routes.length && state.routes[0]?.path !== 'main' && state.routes[0]?.name !== 'main') {
        // Ensure we always load the 'main' (Billboard) screen first, THEN whatever was requested (eg. chat, quick-new, etc.)
        state.routes.unshift(getBillboardRoute());
      }

      // eg When loading directly /main/profile/my-plan, we want to be able to go back to profile-overview with the top-left arrow.
      // So we need to unshift() the profile-overview screen (defined by initialTabRoutes) into the state's routes.
      if (state?.routes.length === 1 && state.routes[0]?.name === 'main') {
        const routes = state.routes[0].state?.routes;
        if (routes?.length === 1) {
          const tab = routes[0]!;
          if (
            tab.state?.routes.length === 1 &&
            typeof tab.state.routes[0]!.path === 'string' &&
            initialTabRoutes[tab.name]
          ) {
            console.log(
              `[App.getStateFromPath] Is a screen inside a tab (${tab.name}); injecting tab's initial route into the routes array.`,
            );
            tab.state.routes.unshift({
              name: initialTabRoutes[tab.name]!,
            });
          }
        }
      }

      console.log('[App.getStateFromPath] returning state:', JSON.stringify(state));
      return state;
    },
    getPathFromState(state, options) {
      // Defensive guard: React Navigation may produce a path without a leading "/".
      // Without it, window.history.replaceState interprets the path as relative and
      // resolves it against the current origin, which can produce a cross-origin URL
      // (e.g. "main/profile/streak" → "https://main/profile/streak") → SecurityError.
      const path = getPathFromState(state, options);
      return isMobileNative ? path : landingUtmTracker.getPath(path);
    },
  };

  const handleDeepLink = useEffectEvent(async (url: string | null) => {
    if (handledDeepLink) {
      return;
    }
    setHandledDeepLink(true);
    setTimeout(() => setHandledDeepLink(false), 5000);
    if (url && isMobileNative) {
      await handleURLCallback(url);
    }
  });
  useEffect(() => {
    const getUrlAsync = async () => {
      const initialUrl = await Linking.getInitialURL();
      await handleDeepLink(initialUrl);
    };

    void getUrlAsync();

    const deepLinkListener = Linking.addEventListener('url', (event: { url: string }) => {
      void handleDeepLink(event.url);
    });

    return () => deepLinkListener.remove();
  }, []);

  function trackGoogleTagManagerPageView(pageName: keyof ScreenParameters) {
    // Startup screens must not consume attribution before the landing screen mounts.
    if (pageName === 'splash' || pageName === 'main' || pageName === 'billboard') return;
    const navigationState = navigationRef.getRootState();
    if (!navigationState) return;
    const pagePath = linking.getPathFromState?.(navigationState, linking.config);
    if (pagePath) {
      pushGoogleTagManagerPageView(pageName, pagePath, landingUtmTracker.takePageViewParams(pagePath));
    }
  }

  const onReady = () => {
    const currentRouteName = navigationRef.getCurrentRoute()?.name as keyof ScreenParameters | undefined;
    routeNameRef.current = currentRouteName ?? null;
    if (currentRouteName) trackGoogleTagManagerPageView(currentRouteName);
    notifyNavigationReady();
  };

  const onStateChange = () => {
    const previousRouteName = routeNameRef.current;
    const currentRoute = navigationRef.getCurrentRoute();

    if (currentRoute) {
      const currentRouteName = currentRoute.name as keyof ScreenParameters;

      const currentRouteParams = currentRoute.params;
      if (previousRouteName !== currentRouteName) {
        screen(currentRouteName, currentRouteParams);
      }

      trackGoogleTagManagerPageView(currentRouteName);
      routeNameRef.current = currentRouteName;
    }
  };

  useEffect(() => {
    changeAppIcon();
  }, []);

  const setLastNotificationResponse = useSetAtom(lastNotificationResponseAtom);

  useEffect(() => {
    if (!notificationResponse) return;
    setLastNotificationResponse(notificationResponse);
  }, [notificationResponse, setLastNotificationResponse]);

  return (
    <DeviceGate>
      <AuthenticationWatcher />
      <BadgeWatcher />
      <LanguageWatcher guestWebLanguage={guestWebLanguage} sessionHydrated={sessionHydrated} />
      <NotificationWatcher />
      <QueryPreloader />

      <View style={{ flex: 1 }}>
        <NavigationContainer
          {...{ linking, onReady, onStateChange }}
          ref={navigationRef}
          theme={{
            ...DefaultTheme,
            colors: {
              ...DefaultTheme.colors,
              background: theme.colors.page,
              card: theme.colors.page,
            },
          }}
        >
          <OfferHubQueueProvider>
            <root.Navigator />
          </OfferHubQueueProvider>
          <GuestWebLoginHost />
        </NavigationContainer>
        <TutorialOverlay />
      </View>

      <ToastProvider />
      <AppDownloadPromptHost />
    </DeviceGate>
  );
}

initializeSentry();
const wrapper = isMobileNative ? SentryMobile.wrap : (RootComponent: React.ComponentType) => RootComponent;

function AppRoot() {
  return (
    <AppWithProviders>
      <App />
    </AppWithProviders>
  );
}

export default wrapper(AppRoot);

function QueryPreloader() {
  useCustomerIO();
  useSentry();
  useMyActivePlans();
  usePaywallAppLaunch();
  // BFF API calls
  useUserProfile();
  useDiscoverScreen();
  return null;
}

function AuthenticationWatcher() {
  const inactiveSince = useRef(0);
  const isStarting = useAtomValue(isStartingAtom);
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const isLoggingOut = useAtomValue(isLoggingOutAtom);
  const userInfo = useAtomValue(userInfoAtom);
  const isOnboarding = useAtomValue(isOnboardingAtom);
  const utmParams = useAtomValue(utmParamsAtom);
  const appStartedLogged = useAtomValue(appStartedLoggedAtom);
  const setAppStartedLogged = useSetAtom(appStartedLoggedAtom);
  const setUtmParams = useSetAtom(utmParamsAtom);
  const pendingAppStartedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const appStartSession = useRef(0);
  const appStartedTrackingScheduled = useRef(appStartedLogged);
  const utmParamsRef = useRef(utmParams);

  useEffect(() => {
    utmParamsRef.current = utmParams;
  }, [utmParams]);

  const [numAppStarts, setNumAppStarts, usedDefault] = useStoredValue<number | undefined>(
    StorageKey.NumAppStarts,
    undefined,
  );

  const numAppStartsLoaded = numAppStarts !== undefined || usedDefault;

  // This will ensure the "User is logged in" code is executed only when userData changes (and on first start).
  // It will also trigger if a log in completes, as userData will then change.
  const { data: userData } = useUser({ enabled: !userInfo });
  const syncAuthenticatedUser = useEffectEvent(() => {
    if (isStarting) return;
    if (!numAppStartsLoaded) return;
    if (isLoggingOut) {
      setNumAppStarts(undefined);
      return;
    }
    if (userData) {
      log(`User is logged in; id=${userData.id}; email=${userData.email}`);
      identify(userData);
      void storage.saveString(StorageKey.CurrentUserId, userData.id).catch(recordError);
      updateSessionState({ currentUserId: userData.id });

      if (numAppStarts === undefined && !isOnboarding) {
        setNumAppStarts(1);
        console.log(`App started for the first time.`);
      }
    }
  });
  useEffect(() => {
    syncAuthenticatedUser();
  }, [isLoggingOut, isStarting, numAppStartsLoaded, userData?.id]);

  const incrementAppStartCount = useEffectEvent(() => {
    // Start counting only once the user is logged-in (they created their account)
    if (numAppStarts !== undefined && numAppStarts >= 1) {
      console.log(`App started ${(numAppStarts + 1).toFixed(0)} times.`);
      setNumAppStarts(numAppStarts + 1);
    }
  });
  useEffect(() => {
    incrementAppStartCount();
  }, [numAppStartsLoaded]);

  // To avoid sending multiple UnexpectedLogout events
  const hasSentUnexpectedLogout = useRef(false);

  useEffect(() => {
    const handleUnexpectedLogout = async () => {
      if (isStarting) return;
      if (isLoggingOut) return;
      if (userInfo) return;
      if (isLoggedIn) return;

      const currentUserId = await storage.readString(StorageKey.CurrentUserId);
      if (currentUserId && !hasSentUnexpectedLogout.current) {
        track(Event.UnexpectedLogout, { source: 'unknown', userId: currentUserId });
        log('User data is null, but we have a current user id; this means the user has been logged out unexpectedly');
        hasSentUnexpectedLogout.current = true;
      }
    };
    void handleUnexpectedLogout();
  }, [isLoggingOut, isStarting, userInfo, isLoggedIn]);

  const refetchBff = useRefetchBff();
  const scheduleAppStartedLog = useEffectEvent(() => {
    if (
      isStarting ||
      AppState.currentState !== 'active' ||
      inactiveSince.current !== 0 ||
      appStartedTrackingScheduled.current
    ) {
      return;
    }

    appStartedTrackingScheduled.current = true;
    setAppStartedLogged(true);
    const scheduledSession = appStartSession.current;

    pendingAppStartedTimer.current = setTimeout(() => {
      if (scheduledSession !== appStartSession.current) return;

      pendingAppStartedTimer.current = null;
      if (AppState.currentState !== 'active' || inactiveSince.current !== 0) {
        appStartedTrackingScheduled.current = false;
        setAppStartedLogged(false);
        return;
      }

      track(Event.AppStarted, utmParamsRef.current);
      trackOnFacebook(CustomFacebookEvent.OpenedApp);
    }, APP_STARTED_DELAY_MS);
  });

  useEffect(() => {
    if (!isStarting) scheduleAppStartedLog();
  }, [isStarting]);

  const handleAppStateChange = useEffectEvent((status: AppStateStatus) => {
    // On Android 12L+, system activities can briefly background the app. Only a real, long background period
    // should refresh active queries; otherwise notification permission checks can create a refetch loop.
    if (status !== 'active') {
      if (inactiveSince.current === 0) inactiveSince.current = Date.now();
      appStartSession.current += 1;
      appStartedTrackingScheduled.current = false;
      if (pendingAppStartedTimer.current !== null) {
        clearTimeout(pendingAppStartedTimer.current);
        pendingAppStartedTimer.current = null;
      }
      setUtmParams(undefined);
      setAppStartedLogged(false);
      return;
    }

    const backgroundStartedAt = inactiveSince.current;
    const wasInactiveLongEnough =
      backgroundStartedAt > 0 && Date.now() - backgroundStartedAt > APP_BACKGROUND_REFETCH_THRESHOLD_MS;
    inactiveSince.current = 0;

    const openForTooLongMs = 18 * 60 * 60 * 1000;
    if (!isMobileNative && Date.now() - APP_STARTED_AT > openForTooLongMs && typeof window !== 'undefined') {
      window.location.reload();
      return;
    }

    if (wasInactiveLongEnough) {
      refetchBff();
      // TanStack already listens to document visibility on web. Native needs this explicit focus transition.
      if (isMobileNative) {
        focusManager.setFocused(false);
        focusManager.setFocused(true);
      }
    }

    scheduleAppStartedLog();
  });

  useEffect(() => {
    const subscription = AppState.addEventListener('change', handleAppStateChange);
    return () => {
      subscription.remove();
      appStartSession.current += 1;
      if (pendingAppStartedTimer.current !== null) clearTimeout(pendingAppStartedTimer.current);
      pendingAppStartedTimer.current = null;
    };
  }, []);

  return null;
}

function BadgeWatcher() {
  const isStarting = useAtomValue(isStartingAtom);
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const isLoggingOut = useAtomValue(isLoggingOutAtom);

  // Count unread messages
  const { data: unreadMessagesData } = useCountUnreadMessages({
    enabled: isLoggedIn,
  });
  const numUnreadMessages = unreadMessagesData?.unread_count ?? 0;

  // App badge count = unread messages
  useEffect(() => {
    if (isStarting) return;
    if (isLoggingOut) return;
    if (!isLoggedIn) {
      console.log('[App]! setBadgeCount: 0 (not logged in)');
      setBadgeCount(0, 'notLoggedIn');
      return;
    }
    console.log(`[App] setBadgeCount: ${numUnreadMessages.toFixed(0)} (messages)`);
    setBadgeCount(numUnreadMessages, 'loggedIn');
  }, [isLoggedIn, isLoggingOut, isStarting, numUnreadMessages]);

  return null;
}

function LanguageWatcher({
  guestWebLanguage,
  sessionHydrated,
}: {
  guestWebLanguage?: ReturnType<typeof getGuestWebLanguageFromLocation>;
  sessionHydrated: boolean;
}) {
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const languagePreferenceQuery = useOnePreference('language');
  const appliedLanguageRef = useRef(i18n.resolvedLanguage);
  const languageWriteQueueRef = useRef(Promise.resolve());

  useEffect(() => {
    if (!sessionHydrated) return;
    const effect = { cancelled: false };
    const isCancelled = () => effect.cancelled;
    const setLanguage = async () => {
      const storedLanguage = await storage.readString(StorageKey.PreferredLang);
      if (isCancelled()) return;
      const nextLanguage =
        (!isLoggedIn ? guestWebLanguage : undefined) ??
        languagePreferenceQuery.data?.value ??
        storedLanguage ??
        getDeviceLanguage();
      const shouldInvalidateLanguageSensitiveQueries = appliedLanguageRef.current !== nextLanguage;

      console.log('[LanguageWatcher] applying language', {
        isLoggedIn,
        guestWebLanguage,
        storedLanguage,
        nextLanguage,
      });

      await i18n.changeLanguage(nextLanguage);
      if (isCancelled()) return;
      const currentWrite = languageWriteQueueRef.current.then(async () => {
        if (isCancelled()) return;
        await storage.saveString(StorageKey.PreferredLang, nextLanguage);
        if (isCancelled()) return;
        appliedLanguageRef.current = nextLanguage;
      });
      languageWriteQueueRef.current = currentWrite.catch(recordError);
      await currentWrite;
      if (isCancelled()) return;

      if (shouldInvalidateLanguageSensitiveQueries) {
        // Invalidate all queries whose API responses are language-sensitive.
        // Listing titles, listing tags, and BFF-preloaded listing payloads all depend on Accept-Language.
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: listingKeys.all }),
          queryClient.invalidateQueries({ queryKey: bffKeys.all }),
          queryClient.invalidateQueries({ queryKey: recommendationKeys.lists() }),
          queryClient.invalidateQueries({ queryKey: interestKeys.lists() }),
        ]);
      }
    };

    void setLanguage();
    return () => {
      effect.cancelled = true;
    };
  }, [guestWebLanguage, isLoggedIn, languagePreferenceQuery.data?.value, sessionHydrated]);

  return null;
}

function NotificationWatcher() {
  const isStarting = useAtomValue(isStartingAtom);
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const messagesPushWatcher = useMessagesPushWatcher();
  const [hasNotificationPermission, setHasNotificationPermission] = useAtom(hasPushNotificationsEnabledAtom);

  const processForegroundRemoteMessage = useEffectEvent(async (remoteMessage: FirebaseMessagingTypes.RemoteMessage) => {
    await onRemoteMessage(remoteMessage, messagesPushWatcher);
  });

  useEffect(() => {
    if (isStarting) return;

    const checkForNotificationPermission = async () => {
      if (!isMobileNative) return;
      const notificationsEnabled = await checkNotificationPermission();
      log('Validating if user has notification permission:', notificationsEnabled);
      setHasNotificationPermission(notificationsEnabled);
    };
    void checkForNotificationPermission();

    if (isLoggedIn && hasNotificationPermission) {
      void pushNotifRegistrationInstance(http).registerForRemoteNotifications();
    }
  }, [hasNotificationPermission, isStarting, setHasNotificationPermission, isLoggedIn]);

  useEffect(() => {
    const unsubscribe =
      Platform.OS !== 'android'
        ? undefined
        : onMessage(getMessaging(), async (remoteMessage: FirebaseMessagingTypes.RemoteMessage) => {
            // For FCM (Android), when app is in foreground
            log('Push Notif received from FCM, in foreground', remoteMessage);
            await processForegroundRemoteMessage(remoteMessage);
            // Signal CIO that this notification has been delivered
            await CustomerIO.pushMessaging.onMessageReceived(remoteMessage, false);
          });

    const subscription = Notifications.addNotificationReceivedListener(async (notification) => {
      if (Platform.OS !== 'ios') return;
      // For APNS (iOS), when app is in foreground
      if (
        notification.request.trigger &&
        'type' in notification.request.trigger &&
        notification.request.trigger.type === 'push'
      ) {
        log('Push Notif received from APNS, in foreground', JSON.stringify(notification));
        if (notification.request.trigger.payload?.aps) {
          const notifPayload = {
            data: (notification.request.trigger.payload.aps as { alert: DMData }).alert,
          } as unknown;
          await processForegroundRemoteMessage(notifPayload as FirebaseMessagingTypes.RemoteMessage);

          // Signal CIO that this notification has been delivered
          const cioPayload = notification.request.trigger.payload;
          console.log(`[CustomerIO.trackNotificationReceived] payload: ${JSON.stringify(cioPayload)}`);
          CustomerIO.pushMessaging.trackNotificationReceived(cioPayload);
        }
      }
    });

    return () => {
      unsubscribe?.();
      subscription.remove();
    };
  }, []);

  return null;
}

const getBillboardThenListingRouting = (id: string) => ({
  routes: [
    getBillboardRoute({
      state: {
        index: 1,
        routes: [{ name: 'landing-page' }, { name: 'one-listing', params: { id } }],
      },
    }),
  ],
});

const getBillboardRoute = (state = {}) => ({
  name: 'main',
  state: {
    index: 0,
    routes: [
      {
        name: 'billboard',
        ...state,
      },
    ],
  },
});
